/**
 * Utility functions for parameter extraction and availability checking
 * in the Prompt Debugger.
 */

/**
 * Control flow keywords that should be excluded from parameter extraction
 */
const CONTROL_FLOW_KEYWORDS = ['if', 'else', 'endif', 'prob', 'endprob'];

/**
 * Extract parameters from a prompt template, excluding control flow syntax and JSON structures.
 *
 * @param template - The prompt template string
 * @returns Array of unique parameter names found in the template
 *
 * @example
 * extractTemplateParams('Hello {{show-name}}, topic: {{show-topic}}')
 * // Returns: ['show-name', 'show-topic']
 *
 * @example
 * extractTemplateParams('{{if #mystyle}}Use {{voice1}}{{endif}}')
 * // Returns: ['voice1'] (excludes {{if}} and {{endif}})
 */
export function extractTemplateParams(template: string): string[] {
  if (!template) return [];

  // Remove JSON-like structures to avoid extracting keys as params
  // Matches patterns like {"key": "value"} or {key: value}
  const templateWithoutJson = template
    .replace(/\{\{[^}]*["'][^}]*\}\}/g, '')
    .replace(/\{\{[^}]*:\s*[^}]*\}\}/g, '');

  // Extract all {{param-name}} patterns
  const matches = templateWithoutJson.match(/\{\{([a-zA-Z0-9_-]+)\}\}/g) || [];
  const params = matches.map(m => m.replace(/\{\{|\}\}/g, ''));

  // Filter out template control flow keywords
  const filtered = params.filter(param => {
    // Exclude exact matches with control flow keywords
    if (CONTROL_FLOW_KEYWORDS.includes(param)) return false;

    // Exclude params starting with control flow (e.g., "if #tag" or "prob 0.5")
    if (param.startsWith('if ') || param.startsWith('prob ')) return false;

    return true;
  });

  // Return unique parameters
  return [...new Set(filtered)];
}

/**
 * Check if a parameter has a value available.
 *
 * @param paramName - The parameter name to check
 * @param availableParams - Object mapping parameter names to their values
 * @returns Object with availability status and value
 *
 * @example
 * getParamAvailability('show-name', { 'show-name': 'My Show' })
 * // Returns: { available: true, value: 'My Show' }
 *
 * @example
 * getParamAvailability('show-name', { 'show-topic': 'Tech' })
 * // Returns: { available: false, value: undefined }
 */
export function getParamAvailability(
  paramName: string,
  availableParams: Record<string, string | number | undefined>
): { available: boolean; value: string | number | undefined } {
  const value = availableParams[paramName];

  return {
    available:
      value !== undefined && value !== null && String(value).trim() !== '',
    value,
  };
}

/**
 * Get all available parameters sorted alphabetically with metadata.
 *
 * @param availableParams - Object mapping parameter names to their values
 * @param templateParams - Array of parameter names currently in the template
 * @returns Array of parameter objects with availability and template status
 *
 * @example
 * getAllAvailableParams(
 *   { 'show-name': 'My Show', 'show-topic': '', 'genre': 'Tech' },
 *   ['show-name', 'genre']
 * )
 * // Returns: [
 * //   { name: 'genre', available: true, inTemplate: true },
 * //   { name: 'show-name', available: true, inTemplate: true },
 * //   { name: 'show-topic', available: false, inTemplate: false }
 * // ]
 */
export function getAllAvailableParams(
  availableParams: Record<string, string | number | undefined>,
  templateParams: string[]
): Array<{ name: string; available: boolean; inTemplate: boolean }> {
  const allParamNames = Object.keys(availableParams);

  return allParamNames
    .map(name => ({
      name,
      available: (() => {
        const value = availableParams[name];
        return (
          value !== undefined && value !== null && String(value).trim() !== ''
        );
      })(),
      inTemplate: templateParams.includes(name),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
